// Password generator. Cryptographically secure via crypto.getRandomValues.

const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "!@#$%^&*()-_=+[]{};:,.<>?/~";

export interface GeneratorOptions {
  length: number;
  lowercase: boolean;
  uppercase: boolean;
  digits: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean; // skip 0/O, 1/l/I
}

const AMBIGUOUS = new Set(["0", "O", "o", "1", "l", "I"]);

function pickIndex(max: number): number {
  // rejection sampling for unbiased index in [0, max)
  const bound = Math.floor(0xffffffff / max) * max;
  const buf = new Uint32Array(1);
  while (true) {
    crypto.getRandomValues(buf);
    if (buf[0] < bound) return buf[0] % max;
  }
}

export function generatePassword(opts: GeneratorOptions): string {
  let alphabet = "";
  const required: string[] = [];
  if (opts.lowercase) {
    alphabet += LOWERCASE;
    required.push(LOWERCASE);
  }
  if (opts.uppercase) {
    alphabet += UPPERCASE;
    required.push(UPPERCASE);
  }
  if (opts.digits) {
    alphabet += DIGITS;
    required.push(DIGITS);
  }
  if (opts.symbols) {
    alphabet += SYMBOLS;
    required.push(SYMBOLS);
  }
  if (opts.excludeAmbiguous) {
    alphabet = [...alphabet].filter((c) => !AMBIGUOUS.has(c)).join("");
  }
  if (alphabet.length === 0) {
    throw new Error("at least one character class must be enabled");
  }

  const out: string[] = [];

  // Guarantee one char from each required class (filtered for ambiguous if needed)
  for (const cls of required) {
    const pool = opts.excludeAmbiguous ? [...cls].filter((c) => !AMBIGUOUS.has(c)) : [...cls];
    if (pool.length === 0) continue;
    out.push(pool[pickIndex(pool.length)]);
  }

  while (out.length < opts.length) {
    out.push(alphabet[pickIndex(alphabet.length)]);
  }

  // Fisher-Yates shuffle (otherwise required chars are always at the front)
  for (let i = out.length - 1; i > 0; i--) {
    const j = pickIndex(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }

  return out.slice(0, opts.length).join("");
}

export function passwordStrength(pw: string): "weak" | "fair" | "good" | "strong" {
  // Rough heuristic — proper zxcvbn integration can come later.
  const len = pw.length;
  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/\d/.test(pw) ? 1 : 0) +
    (/[^a-zA-Z0-9]/.test(pw) ? 1 : 0);
  const score = len * classes;
  if (score < 20) return "weak";
  if (score < 40) return "fair";
  if (score < 64) return "good";
  return "strong";
}
